import { useEffect, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import { SUPPORT_TITLE, withSoftHyphens } from './constants';

import './support.css';

/**
 * Lightweight visual preview of the supporter popup.
 * Shared by the internal setup modal to mirror the external experience.
 */
export default function SupportPreview({ config, supportedUser = null }) {
  const t = useTranslations('common.profile.buttons.support.public');
  const { pitch, backgroundImage, tiers = [], defaultAmounts = [], goal, currency } = config;
  const displayTitle = withSoftHyphens(SUPPORT_TITLE);
  const originalPitch = typeof pitch === 'string' ? pitch : '';
  const [pitchExpanded, setPitchExpanded] = useState(false);
  const [pitchOverflowing, setPitchOverflowing] = useState(false);
  const pitchRef = useRef(null);
  const [frequency, setFrequency] = useState('one-time');
  const [giveAnonymously, setGiveAnonymously] = useState(false);
  const frequencyLabel = frequency === 'monthly' ? t('summary.recurrent') : t('summary.one_time');
  const formatAmount = (value, digits = 0) => formatCurrencyAmount(value, currency, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) || '';

  useEffect(() => {
    setPitchExpanded(false);
    setPitchOverflowing(false);
  }, [originalPitch]);

  useEffect(() => {
    const el = pitchRef.current;
    if (!el) {
      setPitchOverflowing(false);
      return undefined;
    }

    const checkOverflow = () => {
      setPitchOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();

    const rafId = typeof window !== 'undefined' ? window.requestAnimationFrame(checkOverflow) : null;

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(el);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkOverflow);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (typeof window !== 'undefined') {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
        window.removeEventListener('resize', checkOverflow);
      }
    };
  }, [originalPitch, pitchExpanded]);

  const presetAmounts = Array.isArray(defaultAmounts)
    ? defaultAmounts
      .filter(slot => !slot?.isCustom)
      .map(slot => Number(slot.amount))
      .filter(amount => Number.isFinite(amount) && amount >= 3)
    : [];

  const hasCustomSlot = Array.isArray(defaultAmounts) && defaultAmounts.some(slot => slot?.isCustom);

  const displayTiers = tiers.filter(tier => {
    const hasLabel = tier.label && tier.label.trim();
    const hasNote = tier.note && tier.note.trim();
    const numericAmount = Number(tier.amount);
    return hasLabel || hasNote || (!Number.isNaN(numericAmount) && numericAmount >= 1);
  });

  const fallbackAmount =
    presetAmounts[0] ??
    displayTiers.find(tier => Number(tier.amount) >= 1)?.amount ??
    5;
  const goalAmount = goal?.amount ? Number(goal.amount) : null;
  const goalProgressRaw = goal?.currentAmount ?? goal?.current ?? goal?.progress ?? 0;
  const goalProgress = Number.isFinite(Number(goalProgressRaw)) ? Number(goalProgressRaw) : 0;
  const goalProgressPercentage =
    goalAmount && goalAmount > 0 ? Math.min(100, Math.max(0, (goalProgress / goalAmount) * 100)) : 0;
  const summarySubtitle = supportedUser?.user_username
    ? `@${supportedUser.user_username}`
    : supportedUser?.channel_name ?? '';
  const supportedUserProfileImage = resolveUserProfileImage(
    supportedUser?.user_image,
    supportedUser?.userProfileImage,
    supportedUser?.profile_image,
    supportedUser?.image,
  );
  const supportedUserProfileType = resolveUserProfileTypeWithFallback(
    'collector',
    supportedUser?.user_type,
    supportedUser?.__t,
    supportedUser?.type,
    supportedUser?.typeOfAccount,
    supportedUser?.type_of_account,
  );

  const showPreviewPitchToggle = Boolean(originalPitch) && (
    pitchOverflowing ||
    pitchExpanded
  );

  return (
    <div className="support-preview">
      <div className="support-preview__body">
        <div className="support-preview__content">
          <h3 className="support-preview__title">{displayTitle}</h3>
          {backgroundImage?.previewUrl && (
            <div
              className="support-preview__hero"
              style={{ backgroundImage: `url(${backgroundImage.previewUrl})` }}
            />
          )}
          {originalPitch && (
            <div className="support-preview__pitch-wrapper">
              <p
                ref={pitchRef}
                className={`support-preview__pitch${pitchExpanded ? ' support-preview__pitch--expanded' : ' support-preview__pitch--collapsed'}`}
              >
                {originalPitch}
              </p>
              {showPreviewPitchToggle && (
                <button
                  type="button"
                  className="support-preview__pitch-toggle"
                  aria-expanded={pitchExpanded}
                  onClick={() => setPitchExpanded(expanded => !expanded)}
                >
                  {pitchExpanded ? t('actions.show_less') : t('actions.read_more')}
                </button>
              )}
            </div>
          )}

          {supportedUser?.user_name && (
            <div className="support-public__summary">
              <div className="support-public__summary-avatar">
                <UserProfileImage
                  image={supportedUserProfileImage}
                  typeOfProfile={supportedUserProfileType}
                  className="support-public__summary-avatar-image"
                />
              </div>
              <div className="support-public__summary-text">
                <p className="support-public__summary-title">
                  {frequency === 'monthly' ? t('summary.recurring_support_for') : t('summary.one_time_support_for')}{' '}
                  <strong>{supportedUser.user_name}</strong>
                </p>
                <p className="support-public__summary-subtitle">
                  {summarySubtitle}
                </p>
                <p className="support-public__summary-meta">
                  {frequencyLabel} · {giveAnonymously ? t('summary.anonymous_gift') : t('summary.public_support')}
                </p>
              </div>
            </div>
          )}

          <div className="support-public__frequency">
            <button
              type="button"
              className={frequency === 'one-time' ? 'active' : ''}
              onClick={() => setFrequency('one-time')}
            >
              {t('summary.one_time')}
            </button>
            <button
              type="button"
              className={frequency === 'monthly' ? 'active' : ''}
              onClick={() => setFrequency('monthly')}
            >
              {t('summary.monthly')}
            </button>
          </div>

          {displayTiers.length > 0 && (
            <div className="support-preview__tier-list">
              {displayTiers.map(tier => (
                <div key={tier.id} className="support-preview__tier-card">
                  <span className="support-preview__tier-card-amount">
                    {formatAmount(Number(tier.amount || 0), 0)}
                  </span>
                  <div className="support-preview__tier-card-text">
                    {tier.label && <span className="support-preview__tier-card-label">{tier.label}</span>}
                    {tier.note && <span className="support-preview__tier-card-note">{tier.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {(presetAmounts.length > 0 || hasCustomSlot) && (
            <div className="support-preview__tiers">
              {presetAmounts.map((amount, index) => (
                <div key={`preset-${index}`} className="support-preview__tier-chip">
                  {formatAmount(amount, 0)}
                </div>
              ))}
              {hasCustomSlot && (
                <div className="support-preview__tier-chip support-preview__tier-chip--ghost">{t('labels.custom')}</div>
              )}
            </div>
          )}

          <div className="support-public__benefits">
            <ul className="support-public__benefits-list">
              <li>
                {frequency === 'monthly'
                  ? t('benefits.ai_monthly')
                  : t('benefits.ai_one_time')}
              </li>
            </ul>
          </div>

          <label className="support-public__option">
            <input
              type="checkbox"
              checked={giveAnonymously}
              onChange={event => setGiveAnonymously(event.target.checked)}
            />
            {t('summary.gift_anonymously')}
          </label>

          {goalAmount && goal?.label && (
            <div className="support-preview__goal">
              <span className="support-preview__goal-label">{goal.label}</span>
              <div className="support-preview__goal-progress">
                <span className="support-preview__goal-amount">
                  {t('labels.goal_amount', {
                    currencySymbol: '',
                    amount: formatAmount(goalAmount, 0),
                  })}
                </span>
                <div className="support-preview__goal-bar" role="presentation">
                  <div
                    className="support-preview__goal-bar-fill"
                    style={{ width: `${goalProgressPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          <ContinueButton
            disabled={fallbackAmount < 3}>
            {supportedUser?.user_name
              ? t('cta.with_name', {
                name: supportedUser.user_name,
                amount: formatAmount(fallbackAmount >= 3 ? Number(fallbackAmount) : 0, 0)
              })
              : t('cta.default', {
                amount: formatAmount(fallbackAmount >= 3 ? Number(fallbackAmount) : 0, 0)
              })}
          </ContinueButton>
        </div>
      </div>
    </div>
  );
}
